import {
  STATUS_DECLINED,
  STATUS_ACCEPTED,
  ADVISOR_HIGH_CEREST,
  META_DATA,
  BROKER_ALLOCATION_READY,
  STATUS_SUBMITTED,
  BROKER_ALLOCATION,
  OBJECT_META,
  LABELS,
} from 'src/services/constants';
import { RESIDENTIAL_MORTGAGE } from 'src/features/LoansHome/Products/constants';
import { append, concat, equals, filter, flatten, head, includes, isEmpty, lensPath, map, pathOr, replace, set, when } from 'ramda';
import { compose } from 'recompose';
import { timeStampInISOFormat } from 'src/utilities/formatDate';
import { buildLabel, getFullName } from 'src/utilities/naming';
import { base64Encode } from 'src/utilities/base64';
import SiteSettings from 'src/siteSetting';
import { setLabelRedirectURL, THE_APPLICATION, UPDATED } from '../constants/activites';
import { getOneLoanAppURL } from './loanApp';
import { UW_REQUEST_PERMISSION } from 'src/features/RBAC/Rules/permissionData';
import Can from 'src/features/RBAC/Can';
import { describeLoanStatus } from 'src/services/loanStatus';
import { getNamespaceId, getNamespaceName } from './namespaces';

import { hasSubmittedInput } from 'src/features/Advisors/Applications/SummaryNotMortgage/utilities';
import { mkCondition } from './status';
import { mkCompletedCondition } from 'src/utilities/notifications/notificationTemplateBuilder';
import { filterLoanStages } from 'src/features/core/ChatEngine/Configs/lonStagesConfig/config';
import { getAdjacentNodes } from 'src/utilities/tree';
import { namingModificationOfLoanStatusForLoanType } from 'src/components/Widgets/Applications/LoanStages/loanStages';
import { LOAN_STAGES_ACCESS_PERSIMMIONS } from '../constants/accounts';
import { isNotEmpty, returnIsNotEmptyOrTrue } from 'src/utilities/hasValue';
import { mapIndexed } from 'src/utilities/visualFieldsMapper';


export const highcrestDeclineStages = {
  condition_type: `${ADVISOR_HIGH_CEREST}.${STATUS_DECLINED}`,
  message: 'Highcrest declined',
  reason: 'Highcrest.Declined',
  status: true,
  last_update_time: timeStampInISOFormat(),
  last_transition_time: '',
  last_probe_time: '',
};

export const highcrestApprovalStages = {
  condition_type: `${ADVISOR_HIGH_CEREST}.${STATUS_ACCEPTED}`,
  message: 'Highcrest approved',
  reason: 'Highcrest.Approved',
  status: true,
  last_update_time: timeStampInISOFormat(),
  last_transition_time: '',
  last_probe_time: '',
};


export const mkCloneLoanStatus = (loanStatus: any, advisorName?: any, reason?: any) => {
  const currentLoanStatus = pathOr('', ['status', 'phase'], loanStatus);
  const currentReason = pathOr('', ['status', 'reason'], loanStatus);
  const conditions = mkBuildLoanStage(
    currentLoanStatus,
    pathOr([], ['status', 'conditions'], loanStatus),
    advisorName,
    reason
  );
  return compose(
    set(lensPath(['status', 'phase']), currentLoanStatus),
    set(lensPath(['status', 'conditions']), conditions),
    set(lensPath(['status', 'reason']), currentReason)
  )(loanStatus);
};

export const mkReassignLoanStatus = (selectedAdvisor: any, loanStatus: any, assignApprover?: any, retainPermissionOnAssign: boolean =false) => {
  const updateLoanStatus = compose(
    set(
      lensPath([META_DATA, buildLabel('loanapp_assigned_to_advisor_name')]),
      getFullName(selectedAdvisor)
    ),
    set(
      lensPath([META_DATA, buildLabel('loanapp_assigned_to_advisor_persona')]),
      pathOr('', ['persona'], selectedAdvisor)
    ),
    set(
      lensPath([META_DATA, buildLabel('loanapp_assigned_to_advisor_id')]),
      pathOr('', ['id'], selectedAdvisor)
    ),
    set(
      lensPath([META_DATA, buildLabel('loanapp_assigned_to_advisor_email')]),
      pathOr('', ['email'], selectedAdvisor)
    ),
    when(() => isNotEmpty(assignApprover), compose(
      set(
        lensPath([META_DATA, buildLabel('supervisor_assigned_email')]),
        pathOr('', ['email'], selectedAdvisor)
      ),
      set(
        lensPath([META_DATA, buildLabel('supervisor_assigned_name')]),
        getFullName(selectedAdvisor)
      )
    )),

    when(() => equals(retainPermissionOnAssign,false), compose(
      set(
        lensPath([META_DATA, buildLabel('officer_number')]),
        pathOr("", [META_DATA, buildLabel('officer_number')], selectedAdvisor)
      ),
      set(
        lensPath([META_DATA, buildLabel('processor_number')]),
        pathOr("", [META_DATA, buildLabel('processor_number')], selectedAdvisor)
      ),

      set(
        lensPath([META_DATA, buildLabel('branch_code')]),
        pathOr("", ["branches", "branches", 0, "code"], selectedAdvisor)
      )
    )),

  
  )(loanStatus);
  return updateLoanStatus;
};

export const mkUnassignLoanStatus = (loanStatus: any) => {
  const updateLoanStatus = compose(
    set(
      lensPath([META_DATA, buildLabel('loanapp_assigned_to_advisor_name')]),
      ''
    ),
    set(
      lensPath([META_DATA, buildLabel('loanapp_assigned_to_advisor_id')]),
      ''
    ),
    set(
      lensPath([META_DATA, buildLabel('loanapp_assigned_to_advisor_persona')]),
      ''
    ),
    set(
      lensPath([META_DATA, buildLabel('loanapp_assigned_to_advisor_email')]),
      ''
    )
  )(loanStatus);
  return updateLoanStatus;
};

export const mkMismoLoanStatus = (subTypeOfLoan: string, typeOfLoan: string = RESIDENTIAL_MORTGAGE) => {
  const loan_data = {
    type_of_loan: typeOfLoan,
    sub_type_of_loan: subTypeOfLoan,
    context: {
      loan_status: {
        status: {
          phase: SiteSettings.pipelineUseInitialStatus(),
          reason: SiteSettings.pipelineUseInitialStatus(),
          conditions: [
            {
              submitted_by: "",
              condition_type: "Imported.Ready",
              status: true,
              reason: "Imported",
              message: "LO changed to Imported succeeded",
              last_update_time: "",
              last_transition_time: ""
            },
            {
              condition_type: 'Submitted.Ready',
              last_transition_time: '',
              last_update_time: timeStampInISOFormat(),
              message: 'LO changed to Submitted succeeded',
              reason: 'Submitted',
              status: true,
              submitted_by: '',
            },
            {
              condition_type: 'Borrower.Submitted.Ready',
              last_transition_time: '',
              last_update_time: timeStampInISOFormat(),
              message: 'LO changed to Borrower.Submitted succeeded',
              reason: 'Borrower.Submitted',
              status: true,
              submitted_by: '',
            },
          ],
        },
      },
    },
  };
  return base64Encode(loan_data);
};

export const updateLoanStatusWithAccept = async (data: any, loanApp: any, status: any, changeStatus: any, options: any) => {
  const {
    handleLoading,
    thisLoanId,
    thisLoanType,
    handleSetSubmitAppMissingInfo,
    handleSetSubmitInfoModal,
    onCreateAudit,
    advisorFullName,
    describeBusinessByLoanId,
    updateLoanApps,
    permissions,
  } = data;
  const { updateLoanApplicationStatus,
    updateSingleLoanAppInStore,
    _requestAllUWLoanApps,
    _updateDPAdvisorLoanAppsInStore,
    _updateUWAdvisorLoanAppsInStore,
    _updateAdvisorLoanAppsInStore,
    updateAdminLoanApps } = updateLoanApps;
  try {
    handleLoading(true);
    const payload = mkPayload(thisLoanId)
    const { data } = await describeBusinessByLoanId(payload);
    const business = head(pathOr([], ['business'], data));
 
    const missingInfo = hasSubmittedInput(business);
    if (isEmpty(missingInfo)) {
      const loanStatusResponse = await describeLoanStatus(thisLoanId)
      const loanStatus = mkLoanStatus(status, loanStatusResponse, advisorFullName, options)
      const singleLoanStatus = await updateLoanApplicationStatus(loanStatus)

      await map((item: any) => recordAudit(onCreateAudit, thisLoanId, thisLoanType, advisorFullName, item)
        , pathOr([], ['conditions'], options))

      const updateSingleLoan = set(
        lensPath(['spec', 'loan_status', 0]),
        singleLoanStatus,
        loanApp
      );
      updateSingleLoanAppInStore(updateSingleLoan);
      _updateAdvisorLoanAppsInStore(updateSingleLoan);
      updateAdminLoanApps(updateSingleLoan);
      _updateDPAdvisorLoanAppsInStore(updateSingleLoan);
      _updateUWAdvisorLoanAppsInStore(updateSingleLoan);
      // fetch UW loan apps - to maintain the sort order
      Can({
        rules: permissions,
        perform: UW_REQUEST_PERMISSION,
        yes: () => _requestAllUWLoanApps(),
        no: () => null,
      });
    } else {
      handleSetSubmitAppMissingInfo({missingInfo});
      handleSetSubmitInfoModal(true);
    }
  } catch(err: any) {
  } finally {
    handleLoading(false);
  }
}


export const mkPayload = (loanId: any) => {
  return {
    variables: {
      objectMeta: {
        namespace: getNamespaceName(),
        owner_references: [
          {
            uid: loanId
          }
        ]
      }
    }
  }
}

export const recordAudit = (onCreateAudit: any, thisLoanId: any, thisLoanType: any, advisorFullName: any, status: any) => {
  const applicationURL = getOneLoanAppURL(thisLoanId, thisLoanType);
  return onCreateAudit(
    thisLoanId,
    `${advisorFullName}`,
    UPDATED,
    THE_APPLICATION,
    `stage to ${status} of loan ${setLabelRedirectURL('application')}`,
    applicationURL
  )
}

/*As of now lo change the status so the msg is static,if its third party ,change it to the
one who change the status
*/
const LO_STATUS_CHANGE_REASON = (status: any) =>
  `LO changed to ${status} succeeded`;

export const mergeConditionLoanStage = (
  condition: any,
  loanConditions: any
) => {
  return append(condition, loanConditions);
};
export const mkBuildLoanStage = (status: string, loanConditions: any, submittedBy?: any, conditionMessage?: any, condtionReason?: any) => {
  const loanStatus = equals(status, STATUS_DECLINED) ? false : true;
  const message = loanStatus && isEmpty(conditionMessage) ? LO_STATUS_CHANGE_REASON(status) : conditionMessage
  const reason = condtionReason ? condtionReason : '';
  const conditions = mkCondition(
    `${status}.Ready`,
    reason ? replace(/\s/g, '', reason) : '',
    message,
    loanStatus,
    submittedBy
  )
  return mergeConditionLoanStage(
    conditions,
    loanConditions
  );
};



export const mutateLoanStatusCondition = (loanStatuses: any, loanRole: string, loanStatus: any, assigneeName: string) => {
  const loanStatusConditions = pathOr([], ['status', 'conditions'], loanStatus)
  const conditionHistory = mapIndexed((item: any, index: number) => {
    const status = pathOr("", ["status"], item)
    const suffix = pathOr("Ready", ["suffix"], item)
    return mkBuildLoanStageConditions(
      status,
      assigneeName,
      status,
      '',
      suffix
    )
  }, loanStatuses);
  const reason = mkCompletedCondition(loanRole, STATUS_SUBMITTED);
  const mergedConditions = mkBuildLoanStage(
    reason,
    conditionHistory,
    assigneeName,
    reason
  );
  return concat(
    loanStatusConditions,
    mergedConditions
  );
}

export const mkBuildLoanStageIso = (loanConditions: any, advisorName?: any) => {
  return mergeConditionLoanStage(
    mkCondition(
      BROKER_ALLOCATION_READY,
      'Loan permission granted to broker',
      BROKER_ALLOCATION,
      true,
      advisorName
    ),
    loanConditions
  );
};

export const filterMenuOptions = (
  onUpdateLoanApplicationStatus: any,
  thisLoan: any,
  status: any,
  activatePopupFn: any,
  roles: string[],
  allPermissions: any
) => {
  const hasPersonaMatch = filter(item => includes(item, roles), LOAN_STAGES_ACCESS_PERSIMMIONS);
  if (hasPersonaMatch) {
    const hasActiveMenu = (x: any) => {
      return !equals(
        namingModificationOfLoanStatusForLoanType(x.data.value),
        namingModificationOfLoanStatusForLoanType(status)
      );
    };
    const menuOptions = flatten([
      ...getMenuOptionForLoanType(thisLoan, roles, allPermissions),
    ]);
    const filteredOptions = filter(hasActiveMenu, menuOptions);

    const data = map((x: any) => {
      return menuActions(
        x,
        onUpdateLoanApplicationStatus,
        thisLoan,
        activatePopupFn
      );
    }, filteredOptions);

    return data;
  }
  return [];
};

const menuActions = (
  x: any,
  onUpdateLoanApplicationStatus: any,
  thisLoan: any,
  activatePopupFn: any
) => {
  const options = pathOr({}, ['data', 'options'], x)
  return {
    name: pathOr('', ['data', 'name'], x),
    onClick: () => {
      return onUpdateLoanApplicationStatus(activatePopupFn, thisLoan, pathOr('', ['data', 'value'], x), options);
    },
    className: '',
    options: options,
  };

};
const getMenuOptionForLoanType = (thisLoan: any, roles: string[], permissions: any) => {
  const loanType = pathOr('', ['type_of_loan'], thisLoan);
  // TODO: we should use common one flage sub_type_of_loan instead of using multiple flags 
  // we have to request API to use sub_type_of_loan 
  const loanProductType = pathOr('', ['product_type'], thisLoan) ||
    pathOr('', ['sub_type_of_loan'], thisLoan) ||
    pathOr('', ['spec', 'properties', OBJECT_META, LABELS, 'property_loan_type'], thisLoan)


  const loanStatus = pathOr(
    '',
    ['spec', 'loan_status', 0, 'status', 'phase'],
    thisLoan
  );
  const loanStages = filterLoanStages(
    getNamespaceId(),
    loanType,
    roles,
    returnIsNotEmptyOrTrue(
      loanProductType,
      loanProductType
    ),
    permissions,
    thisLoan
  );
  return getAdjacentNodes(pathOr({}, ['stages'], loanStages), loanStatus);
};

const mkLoanStatus = (status: any, loanStatuses: any, advisorName: any, options: any) => {
  const loanStatus = head(pathOr('', ['items'], loanStatuses))
  const conditions = mkBuildLoanStage(
    status,
    pathOr([], ['status', 'conditions'], loanStatus),
    advisorName,
    status
  );
  const updCondtions = map((item: any) => mkBuildLoanStage(
    item,
    conditions,
    advisorName,
    item
  ), pathOr([], ['conditions'], options));
  const data = compose(
    set(lensPath(['status', 'phase']), pathOr('', ['phase'], options)),
    set(lensPath(['status', 'conditions']), head(updCondtions)),
    set(
      lensPath([
        META_DATA,
        buildLabel('loanapp_assigned_to_advisor_email'),
      ]),
      ''
    ),
    set(
      lensPath([META_DATA, buildLabel('loanapp_assigned_to_advisor_id')]),
      ''
    ),
    set(
      lensPath([
        META_DATA,
        buildLabel('loanapp_assigned_to_advisor_persona'),
      ]),
      ''
    ),
    set(
      lensPath([
        META_DATA,
        buildLabel('loanapp_assigned_to_advisor_name'),
      ]),
      ''
    )
  )(loanStatus);
  return data;
}
export const mkBuildLoanStageConditions = (status: string, advisorName?: any, conditionMessage?: any, condtionReason?: any, suffixCondition = "Ready") => {
  const loanStatus = equals(STATUS_DECLINED, status) ? false : true;
  const message = loanStatus && isEmpty(conditionMessage) ? LO_STATUS_CHANGE_REASON(status) : conditionMessage
  const reason = condtionReason ? condtionReason : status;
  const conditions = mkCondition(
    `${status}.${suffixCondition}`,
    reason ? replace(/\s/g, '', reason) : '',
    message,
    loanStatus,
    advisorName
  )
  return conditions;

};